import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { ModalService } from '@base/services/private/modal.service';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { ProfileSrvAbstract } from '@base/abstracts/services/profile.abstract';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { UpdateCompanyBrochureRequest } from '@base/models/requests/profle-me.req';
import { ProfileCompanyDetail } from '@base/models/responses/profile-me.res';
import { Brochure } from '@base/models/responses/brochure.res';
import type { DocViewerComponent } from '@base/components/private/modals/doc-viewer/doc-viewer.component';
import { ChangeLogCompanyBrochures } from '@base/models/responses/profile-company.res';
import { Subscription, take } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { FileSize } from '@base/constants/file-size';
import { ThemeService } from '@base/services/private/theme.service';

@Component({
  selector: 'app-modal-upload-company-brochure',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ReactiveFormsModule,
    ModalFooterComponent,
    forwardRef(() => TranslatePipe),
  ],
  templateUrl: './modal-upload-company-brochure.component.html',
  styleUrl: './modal-upload-company-brochure.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalUploadCompanyBrochureComponent implements AfterViewInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly fb = inject(FormBuilder);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly globalSrv = inject(GlobalService);
  private readonly themeSrv = inject(ThemeService);
  private profileSrv!: ProfileSrvAbstract;

  @Input() modalId = '';
  @Input() companyData: ProfileCompanyDetail | null = null;
  @Input() brochure: Brochure | null = null;
  @Input() editSource = '';

  changeLogCompanyBrochures: ChangeLogCompanyBrochures | null = null;
  isBgDarkMode = this.themeSrv.isBgDarkMode ?? false;
  isHttpRequestRunning = false;
  httpRequestObserver$: Subscription | null = null;
  form = this.fb.group({
    title: ['', [Validators.required]],
    file: [null as File | null, [Validators.required]],
  });
  activeButton: 'save' | 'remove' | null = null;
  errorMessage = '';

  ngAfterViewInit() {
    (async () => {
      await this.loadServices();
    })();
    this.listenHttpRequestState();
    this.form.patchValue({
      title: this.brochure?.title || '',
    });
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.markToDetectChanges();
      },
    });
  }

  /**
   * Closes the modal with the given modalId.
   * @param isUpdated - Indicates if the brochure data was updated
   * @returns void
   */
  close(isUpdated = false): void {
    this.modalSrv.close(this.modalId, { key: 'MODAL_EDIT_BROCHURES', data: isUpdated });
  }

  /**
   * Handles the form submission for uploading or editing a company brochure.
   *
   * - Marks all form fields as touched for validation.
   * - If the form is valid, constructs the payload from form values.
   * - Calls the `updateCompanyBrochure` method from the profile service.
   * - On success, closes the modal and triggers change detection.
   * - On error, logs the error to the console.
   *
   * @returns Promise<void>
   */
  async onSubmit() {
    this.form.markAllAsTouched();
    this.form.markAllAsDirty();
    if (this.form.invalid) {
      return;
    }
    this.activeButton = 'save';

    const data = this.form.getRawValue();
    const payload: UpdateCompanyBrochureRequest = {
      title: data.title!,
      file: data.file,
      display_order: this.brochure!.display_order!,
    };

    this.profileSrv
      .updateCompanyBrochure(this.companyData ? this.companyData!.id_object! : '', payload, this.editSource)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const status = res?.status;
          if (status === 1) {
            this.close(true);
            this.markToDetectChanges();
          }

          if (status === -2) {
            this.errorMessage = 'YOU_REACHED_MAXIMUM_UPLOADS';
          }
        },
        error: (error) => {
          console.error('Error uploading company brochure:', error);
          this.errorMessage = 'SOMETHING_WENT_WRONG';
        },
        complete: () => {
          this.form.reset();
          this.activeButton = null;
          this.markToDetectChanges();
        },
      });
  }

  get title() {
    return this.form.get('title') as FormControl;
  }

  get file() {
    return this.form.get('file') as FormControl;
  }

  /**
   * Handles file input changes for brochure upload.
   *
   * - Accepts only PDF files.
   * - Validates file size (max 10MB).
   * - Sets validation errors if file is too large.
   * - Updates the form control with the selected file.
   * - Triggers change detection.
   *
   * @param event - The file input change event
   * @returns void
   */
  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files && input.files.length ? input.files[0] : null;
    if (selectedFile && selectedFile.type.startsWith('application/pdf')) {
      const reader = new FileReader();
      reader.onload = () => {
        if (selectedFile.size > FileSize['10MB']) {
          this.form.get('file')?.setErrors({ size: true });
          input.value = '';
          return;
        } else {
          this.form.get('file')?.setValue(selectedFile);
          this.markToDetectChanges();
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  }

  /**
   * Triggers Angular change detection and marks the component for check.
   *
   * Ensures that updates to the component's data or state are reflected in the view,
   * especially when using OnPush change detection strategy.
   *
   * @returns void
   */
  private markToDetectChanges(): void {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Loads necessary services for the app.
   *
   * @returns Promise<void> Resolves when all services are loaded.
   */
  private async loadServices(): Promise<void> {
    try {
      const services = await Promise.all([this.dynamicImportLoaderSrv.loadService(WhiteLabel.SERVICES.Profile)]);

      if (services) {
        const [profileSrv] = services;
        this.profileSrv = profileSrv as ProfileSrvAbstract;
        if (this.profileSrv) {
          this.getChangeLog();
        }
      } else {
        throw new Error('Services could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  }

  /**
   * Retrieves the change log for company brochures if editing the company source.
   *
   * - Calls the profile service to get the change log.
   * - Updates the local state and triggers change detection.
   *
   * @returns void
   */
  getChangeLog(): void {
    if (this.editSource === 'company') {
      this.profileSrv
        .companyBrochuresChangeLog(this.companyData ? this.companyData!.id_object! : '')
        .pipe(take(1))
        .subscribe({
          next: (res: ChangeLogCompanyBrochures) => {
            this.changeLogCompanyBrochures = res;

            this.markToDetectChanges();
          },
        });
    }
  }

  /**
   * Opens a brochure document in the DocViewer modal.
   *
   * - Passes the PDF source URL to the DocViewer modal.
   *
   * @param urlDoc - The URL of the brochure document (PDF) to display.
   * @returns void
   */
  openBrochure(urlDoc: string): void {
    const data: Partial<DocViewerComponent> = {
      pdfSrc: urlDoc,
    };
    void this.modalSrv.showModalDocViewer({
      addsData: data,
    });
  }

  /**
   * Deletes the company brochure asset from the server.
   *
   * - Calls the profile service to delete the asset.
   * - On success, closes the modal and triggers change detection.
   * - On error, logs the error to the console.
   *
   * @returns void
   */
  deleteBrochure(brochureId: number) {
    this.activeButton = 'remove';

    this.profileSrv
      .deleteCompanyBrochure(this.companyData ? this.companyData!.id_object! : '', brochureId, this.editSource)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.close(true);
          this.markToDetectChanges();
        },
        error: (error) => {
          console.error('Error deleting company asset:', error);
          this.errorMessage = 'SOMETHING_WENT_WRONG';
        },
        complete: () => {
          this.activeButton = null;
          this.markToDetectChanges();
        },
      });
  }
}
